import { AlertTriangle, Inbox } from 'lucide-react';
import { formatarPct, formatarReais } from '@profitlens/calculo';
import { GradeIndicadores, Indicador } from '../componentes/Indicador';
import { Carregando, Erro, Vazio } from '../componentes/Estados';
import { Cabecalho } from '../componentes/Cabecalho';
import { usePainel } from '../servicos/painel';

export function Painel() {
  const { resumo, carregando, erro } = usePainel();

  if (carregando) return <Carregando />;
  if (erro) return <Erro titulo="Não foi possível carregar o painel" detalhe={erro} />;

  if (!resumo) {
    return (
      <>
        <Cabecalho titulo="Painel" descricao="Os indicadores da operação num relance." />
        <Vazio
          icone={<Inbox className="size-8" />}
          titulo="Ainda não há dados para resumir"
          descricao="O painel é montado a partir dos seus produtos, cotações e análises. Cadastre uma mercadoria e um fornecedor para começar."
        />
      </>
    );
  }

  const alertas = [
    resumo.cotacoesDesatualizadas > 0 &&
      `${resumo.cotacoesDesatualizadas} ${resumo.cotacoesDesatualizadas === 1 ? 'cotação vencida' : 'cotações vencidas'}`,
    resumo.itensAguardandoRevisao > 0 &&
      `${resumo.itensAguardandoRevisao} ${resumo.itensAguardandoRevisao === 1 ? 'item aguarda' : 'itens aguardam'} revisão humana`,
  ].filter(Boolean) as string[];

  return (
    <div className="flex flex-col gap-6">
      <Cabecalho titulo="Painel" descricao="Os indicadores da operação num relance." />

      <GradeIndicadores>
        <Indicador rotulo="Produtos cadastrados" valor={resumo.produtosCadastrados} para="/mercadorias" />
        <Indicador rotulo="Fornecedores ativos" valor={resumo.fornecedoresAtivos} para="/fornecedores" />
        <Indicador
          rotulo="Cotações aguardando"
          valor={resumo.cotacoesAguardando}
          para="/cotacoes?status=ENVIADA"
        />
        <Indicador
          rotulo="Oportunidades viáveis"
          valor={resumo.oportunidadesViaveis}
          para="/oportunidades?viaveis=1"
        />
        <Indicador
          rotulo="Capital necessário"
          valor={formatarReais(resumo.capitalNecessarioCents)}
          rodape="somando as oportunidades em aberto"
        />
        <Indicador rotulo="Lucro potencial" valor={formatarReais(resumo.lucroPotencialCents)} />
        <Indicador rotulo="Margem média" valor={formatarPct(resumo.margemMediaBps)} />
        <Indicador
          rotulo="Cotações desatualizadas"
          valor={resumo.cotacoesDesatualizadas}
          alerta={resumo.cotacoesDesatualizadas > 0}
          para="/cotacoes?status=EXPIRADA"
        />
      </GradeIndicadores>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="flex flex-col gap-3 rounded-md border border-regua-2 bg-superficie p-4">
          <h2 className="font-mono text-[0.65rem] tracking-[0.1em] text-marca uppercase">
            Responsáveis por oportunidade
          </h2>
          {resumo.responsaveis.length === 0 ? (
            <p className="text-sm text-tinta-3">Nenhuma oportunidade tem responsável ainda.</p>
          ) : (
            <ul className="flex flex-col gap-1.5 text-sm">
              {resumo.responsaveis.map((r) => (
                <li key={r.usuarioId} className="flex items-baseline justify-between gap-2">
                  <span>{r.nome}</span>
                  <span className="num text-tinta-2">
                    {r.total}
                    {r.emNegociacao > 0 && (
                      <span className="ml-1 text-[0.7rem] text-tinta-3">
                        ({r.emNegociacao} em negociação)
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="flex flex-col gap-3 rounded-md border border-regua-2 bg-superficie p-4">
          <h2 className="font-mono text-[0.65rem] tracking-[0.1em] text-marca uppercase">
            Compras em acompanhamento
          </h2>
          {resumo.comprasEmAcompanhamento === 0 ? (
            <p className="text-sm text-tinta-3">
              Nenhuma compra registrada. Depois de comprar, registre aqui para comparar o resultado
              estimado com o real.
            </p>
          ) : (
            <p className="num text-2xl font-semibold">{resumo.comprasEmAcompanhamento}</p>
          )}
        </section>
      </div>

      {alertas.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border-l-3 border-pend bg-pend/10 px-4 py-3 text-sm"
          role="status"
        >
          <AlertTriangle className="size-4 shrink-0 text-pend" aria-hidden />
          {alertas.map((a, i) => (
            <span key={a} className="text-tinta-2">
              {i > 0 && <span className="mr-3 text-tinta-3">·</span>}
              {a}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
